import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { BellOff } from "lucide-react";

export interface JournalActionValue {
  /** What to do next, e.g. "Call Jane". */
  action: string;
  /** Value for a datetime-local input, or "" for no reminder. */
  reminderAt: string;
}

interface JournalActionFieldsProps {
  value: JournalActionValue;
  onChange: (value: JournalActionValue) => void;
  disabled?: boolean;
  /** Distinguishes the create form from an edit form on the same page. */
  idPrefix: string;
}

/**
 * The follow up half of a journal entry: what to do next, and when to be
 * reminded. Shared by the new entry form and the inline editor so both write
 * the same shape.
 */
export function JournalActionFields({
  value,
  onChange,
  disabled,
  idPrefix,
}: JournalActionFieldsProps) {
  const actionId = `${idPrefix}-action`;
  const reminderId = `${idPrefix}-reminder`;

  return (
    <div className="grid gap-3 sm:grid-cols-2">
      <div className="space-y-1.5">
        <Label htmlFor={actionId}>Action</Label>
        <Input
          id={actionId}
          placeholder="e.g. Call Jane"
          value={value.action}
          disabled={disabled}
          onChange={(e) => onChange({ ...value, action: e.target.value })}
        />
      </div>

      <div className="space-y-1.5">
        <Label htmlFor={reminderId}>Remind me</Label>
        <div className="flex gap-2">
          <Input
            id={reminderId}
            type="datetime-local"
            value={value.reminderAt}
            disabled={disabled}
            onChange={(e) => onChange({ ...value, reminderAt: e.target.value })}
          />
          {value.reminderAt && (
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="shrink-0"
              disabled={disabled}
              aria-label="Clear reminder"
              onClick={() => onChange({ ...value, reminderAt: "" })}
            >
              <BellOff className="h-4 w-4" />
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/** datetime-local gives local wall time; the API wants an ISO offset instant. */
export function toReminderIso(reminderAt: string): string | null {
  if (!reminderAt) {
    return null;
  }
  const parsed = new Date(reminderAt);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/** Turns the stored instant back into a value a datetime-local input accepts. */
export function toReminderInputValue(reminderAt: string | null): string {
  if (!reminderAt) {
    return "";
  }
  const parsed = new Date(reminderAt);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }
  // Offset to local time so the input shows the time the admin picked.
  const local = new Date(
    parsed.getTime() - parsed.getTimezoneOffset() * 60_000,
  );
  return local.toISOString().slice(0, 16);
}
